import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import BasicCli from '../../main/components/basicCli/BasicCli';
import GeneralTable from '../../main/components/grid/GeneralTable';
import RightPanel from '../../main/components/rightPanel/RightPanel';
import EditModal from '../../main/components/modal/edit/EditModal';
import DeleteModal from '../../main/components/modal/delete/DeleteModal';
import eFormType from '../../main/assets/enums/eFormType';
import { ApiConstants } from '../../main/constants/ApiConstants';
import axiosInstance from '../../main/initializers/axios';
import IGridItems from '../../main/interfaces/IGridItems';
import IPagination from '../../main/interfaces/IPagination';
import IExpenseCategory from '../../main/interfaces/IExpenseCategory';
import IExpenseInvoice, { IExpenseInvoiceItem } from '../../main/interfaces/IExpenseInvoice';
import ISubject from '../../main/interfaces/ISubject';
import ExpenseCategories from './ExpenseCategories';
import './Expenses.css';

const EXPENSE_INVOICE_GRID_ENTITY = 23;

type InvoiceFormModel = {
  id: number;
  invoiceNo: string;
  invoiceDate: string;
  expenseCategoryId: number;
  subjectId: string;
  paymentMethod: string;
  description: string;
  vatAmount: number;
  items: IExpenseInvoiceItem[];
};

const newItem = (): IExpenseInvoiceItem => ({
  id: 0,
  description: '',
  quantity: 1,
  price: 0,
  total: 0,
});

const emptyInvoice = (): InvoiceFormModel => ({
  id: 0,
  invoiceNo: '',
  invoiceDate: new Date().toISOString().slice(0, 10),
  expenseCategoryId: 0,
  subjectId: '',
  paymentMethod: 'Cash',
  description: '',
  vatAmount: 0,
  items: [newItem()],
});

const Expenses = () => {
  const { i18n } = useTranslation();
  const [rows, setRows] = useState<IExpenseInvoice[]>([]);
  const [categories, setCategories] = useState<IExpenseCategory[]>([]);
  const [subjects, setSubjects] = useState<ISubject[]>([]);
  const [selected, setSelected] = useState<IExpenseInvoice | null>(null);
  const [form, setForm] = useState<InvoiceFormModel>(emptyInvoice());
  const [gridItems, setGridItems] = useState<IGridItems[]>([]);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [pagination, setPagination] = useState<IPagination>({
    count: 1,
    page: 1,
    itemPerPage: 50,
    itemsPerPage: [10, 20, 50, 100],
  });

  const totals = useMemo(() => {
    const amountWithoutVat = form.items.reduce((sum, item) => sum + Number(item.total || 0), 0);
    const vatAmount = Number(form.vatAmount || 0);
    return {
      amountWithoutVat,
      vatAmount,
      totalAmount: amountWithoutVat + vatAmount,
    };
  }, [form.items, form.vatAmount]);

  const fetchCategories = () => {
    axiosInstance
      .get(ApiConstants.expenses.categories.list, { params: { onlyActive: true } })
      .then((res) => setCategories(res.data.items ?? res.data ?? []))
      .catch(() => toast.error(i18n.t('expenses.fetchCategoriesError')));
  };

  const fetchSubjects = () => {
    axiosInstance
      .get(ApiConstants.invoice.subjects)
      .then((res) => setSubjects(Array.isArray(res.data) ? res.data : res.data?.items ?? []))
      .catch(() => toast.error(i18n.t('errors.somethingWentWrong')));
  };

  const fetchRows = (pgOverride?: IPagination, gridOverride?: IGridItems[]) => {
    const pg = pgOverride ?? pagination;
    axiosInstance
      .post(ApiConstants.expenses.invoices.listct, {
        gridItems: gridOverride ?? gridItems,
        pagination: pg,
      })
      .then((res) => {
        setRows(res.data.items ?? []);
        setGridItems(res.data.gridItems ?? []);
        setPagination({ ...pg, count: res.data.pagination?.count ?? pg.count });
      })
      .catch(() => toast.error(i18n.t('errors.somethingWentWrong')));
  };

  useEffect(() => {
    fetchCategories();
    fetchSubjects();
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setField = <K extends keyof InvoiceFormModel>(key: K, value: InvoiceFormModel[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const setLine = (index: number, patch: Partial<IExpenseInvoiceItem>) => {
    setForm((prev) => {
      const items = prev.items.map((item, i) => {
        if (i !== index) return item;
        const next = { ...item, ...patch };
        return {
          ...next,
          total: roundMoney(Number(next.quantity || 0) * Number(next.price || 0)),
        };
      });
      return { ...prev, items };
    });
  };

  const openAdd = () => {
    setSelected(null);
    setForm(emptyInvoice());
    setIsAddOpen(true);
  };

  const openEdit = (invoice: IExpenseInvoice) => {
    axiosInstance
      .get(ApiConstants.expenses.invoices.byId(invoice.id))
      .then((res) => {
        openEditModel(res.data ?? invoice);
      })
      .catch(() => {
        openEditModel(invoice);
      });
  };

  const openEditModel = (invoice: IExpenseInvoice) => {
    setSelected(invoice);
    setForm({
      id: invoice.id,
      invoiceNo: invoice.invoiceNo ?? '',
      invoiceDate: (invoice.invoiceDate ?? '').slice(0, 10),
      expenseCategoryId: invoice.expenseCategoryId,
      subjectId: invoice.subjectId ?? '',
      paymentMethod: invoice.paymentMethod ?? 'Cash',
      description: invoice.description ?? '',
      vatAmount: Number(invoice.vatAmount ?? 0),
      items: invoice.items?.length ? invoice.items.map((item) => ({ ...item })) : [newItem()],
    });
    setIsEditOpen(true);
  };

  const validate = () => {
    if (!form.expenseCategoryId) {
      toast.error(i18n.t('expenses.categoryRequired'));
      return false;
    }
    if (!form.invoiceDate) {
      toast.error(i18n.t('expenses.dateRequired'));
      return false;
    }
    if (totals.totalAmount <= 0) {
      toast.error(i18n.t('expenses.totalRequired'));
      return false;
    }
    if (form.items.some((item) => Number(item.quantity) <= 0 || Number(item.price) <= 0)) {
      toast.error(i18n.t('expenses.linesRequired'));
      return false;
    }
    return true;
  };

  const save = () => {
    if (!validate()) return;

    const payload: IExpenseInvoice = {
      id: form.id,
      invoiceNo: form.id ? form.invoiceNo || null : null,
      invoiceDate: form.invoiceDate,
      expenseCategoryId: Number(form.expenseCategoryId),
      subjectId: form.subjectId || null,
      paymentMethod: form.paymentMethod || null,
      description: form.description || null,
      amountWithoutVat: roundMoney(totals.amountWithoutVat),
      vatAmount: roundMoney(totals.vatAmount),
      totalAmount: roundMoney(totals.totalAmount),
      items: form.items.map((item) => ({
        id: item.id ?? 0,
        description: item.description || null,
        quantity: Number(item.quantity),
        price: Number(item.price),
        total: roundMoney(Number(item.quantity) * Number(item.price)),
      })),
    };

    axiosInstance
      .post(ApiConstants.expenses.invoices.save, payload)
      .then(() => {
        toast.success(form.id ? i18n.t('expenses.invoiceUpdated') : i18n.t('expenses.invoiceCreated'));
        setIsAddOpen(false);
        setIsEditOpen(false);
        setSelected(null);
        fetchRows();
      })
      .catch((error) => toast.error(error?.response?.data?.message ?? i18n.t('errors.somethingWentWrong')));
  };

  const deleteItem = () => {
    if (!selected?.id) return;

    axiosInstance
      .delete(ApiConstants.expenses.invoices.delete(selected.id))
      .then(() => {
        toast.success(i18n.t('expenses.invoiceDeleted'));
        setIsDeleteOpen(false);
        setSelected(null);
        fetchRows();
      })
      .catch((error) => toast.error(error?.response?.data?.message ?? i18n.t('errors.somethingWentWrong')));
  };

  return (
    <BasicCli>
      <div className="expensePage bcPageContent">
        <div className="expenseContainer">
          <div className="bcPageHeader expenseHeader">
            <div>
              <h1 className="bcPageTitle">{i18n.t('expenses.invoicesTitle')}</h1>
              <p className="expenseSubtitle">{i18n.t('expenses.invoicesSubtitle')}</p>
            </div>
            <div className="bcPageHeaderActions">
              <button type="button" className="ui-control ui-btn ui-btn--primary" onClick={openAdd}>
                {i18n.t('expenses.addInvoice')}
              </button>
              <button type="button" className="ui-control ui-btn ui-btn--secondary" onClick={() => setIsCategoryOpen(true)}>
                {i18n.t('expenses.addCategory')}
              </button>
            </div>
          </div>

          <div className="expenseTable">
            <GeneralTable
              data={rows}
              columns={gridItems}
              gridEntity={EXPENSE_INVOICE_GRID_ENTITY}
              title=""
              hasRp
              selectedItem={(val) => setSelected(val)}
              onEdit={openEdit}
              onDelete={(item) => {
                setSelected(item);
                setIsDeleteOpen(true);
              }}
              pagination={pagination}
              selectedItemPagination={(val: IPagination) => {
                setPagination(val);
                fetchRows(val);
              }}
              onFilterClose={(nextGridItems) => {
                setGridItems(nextGridItems);
                fetchRows(undefined, nextGridItems);
              }}
            />
          </div>
        </div>

        <RightPanel
          isOpen={isCategoryOpen}
          size={52}
          type="right"
          panelContainerClassName="expenseCategoryPanelShell"
          panelClassName="expenseCategoryPanel"
          onKill={() => {
            setIsCategoryOpen(false);
            fetchCategories();
          }}
          backdropClicked={() => {
            setIsCategoryOpen(false);
            fetchCategories();
          }}
        >
          <ExpenseCategories embedded onChanged={fetchCategories} />
        </RightPanel>

        {(isAddOpen || isEditOpen) && (
          <EditModal
            title={isEditOpen ? i18n.t('expenses.editInvoice') : i18n.t('expenses.addInvoice')}
            width="980px"
            height="86vh"
            overlayClassName="expenseEditModalOverlay"
            containerClassName="expenseEditModal"
            closeModal={() => {
              setIsAddOpen(false);
              setIsEditOpen(false);
            }}
            onSubmitData={save}
            setIsModalOpen={(val) => {
              setIsAddOpen(val);
              setIsEditOpen(val);
            }}
            modalType={isEditOpen ? eFormType.Edit : eFormType.New}
          >
            <ExpenseInvoiceForm
              form={form}
              categories={categories}
              subjects={subjects}
              totals={totals}
              setField={setField}
              setLine={setLine}
              addLine={() => setForm((prev) => ({ ...prev, items: [...prev.items, newItem()] }))}
              removeLine={(index) => setForm((prev) => ({ ...prev, items: prev.items.filter((_, i) => i !== index) || [newItem()] }))}
            />
          </EditModal>
        )}

        {isDeleteOpen && selected && (
          <DeleteModal width="500px" handleClose={() => setIsDeleteOpen(false)} handleDelete={deleteItem}>
            {i18n.t('expenses.deleteInvoiceConfirm', { invoiceNo: selected.invoiceNo || selected.id })}
          </DeleteModal>
        )}
      </div>
    </BasicCli>
  );
};

const ExpenseInvoiceForm: React.FC<{
  form: InvoiceFormModel;
  categories: IExpenseCategory[];
  subjects: ISubject[];
  totals: { amountWithoutVat: number; vatAmount: number; totalAmount: number };
  setField: <K extends keyof InvoiceFormModel>(key: K, value: InvoiceFormModel[K]) => void;
  setLine: (index: number, patch: Partial<IExpenseInvoiceItem>) => void;
  addLine: () => void;
  removeLine: (index: number) => void;
}> = ({ form, categories, subjects, totals, setField, setLine, addLine, removeLine }) => {
  const { i18n } = useTranslation();

  return (
    <div className="expenseInvoiceForm">
      <div className="expenseFormGrid">
        <Field className="expenseField--date" label={i18n.t('expenses.invoiceDate')}>
          <input className="expenseInput" type="date" value={form.invoiceDate} onChange={(e) => setField('invoiceDate', e.target.value)} />
        </Field>
        <Field className="expenseField--category" label={i18n.t('expenses.category')}>
          <select className="expenseInput" value={form.expenseCategoryId} onChange={(e) => setField('expenseCategoryId', Number(e.target.value))}>
            <option value={0}>{i18n.t('generic.-select-')}</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </Field>
        <Field className="expenseField--subject" label={i18n.t('expenses.subject', { defaultValue: 'Subjekti' })}>
          <select className="expenseInput" value={form.subjectId} onChange={(e) => setField('subjectId', e.target.value)}>
            <option value="">{i18n.t('generic.-select-')}</option>
            {subjects.map((subject) => {
              const nipt = subject.nipt || (subject as any).NIPT;
              return (
                <option key={subject.id} value={subject.id}>
                  {subject.name}{nipt ? ` - ${nipt}` : ''}
                </option>
              );
            })}
          </select>
        </Field>
        <Field className="expenseField--payment" label={i18n.t('expenses.paymentMethod')}>
          <select className="expenseInput" value={form.paymentMethod} onChange={(e) => setField('paymentMethod', e.target.value)}>
            <option value="Cash">{i18n.t('paymentMethod.cash')}</option>
            <option value="Card">{i18n.t('paymentMethod.card')}</option>
            <option value="Bank">{i18n.t('paymentMethod.bank')}</option>
            <option value="Online">{i18n.t('paymentMethod.online')}</option>
          </select>
        </Field>
        <Field className="expenseField--vat" label={i18n.t('expenses.vatAmount')}>
          <input className="expenseInput" type="number" step="0.01" value={form.vatAmount} onChange={(e) => setField('vatAmount', Number(e.target.value || 0))} />
        </Field>
        <Field className="expenseField--description" label={i18n.t('generic.description')}>
          <textarea className="expenseTextarea" rows={2} value={form.description} onChange={(e) => setField('description', e.target.value)} />
        </Field>
      </div>

      <div className="expenseLinesHeader">
        <h3>{i18n.t('expenses.lines')}</h3>
        <button type="button" className="ui-control ui-btn ui-btn--secondary" onClick={addLine}>
          {i18n.t('expenses.addLine')}
        </button>
      </div>

      <div className="expenseLines">
        <div className="expenseLine isHead">
          <span>{i18n.t('generic.description')}</span>
          <span>{i18n.t('expenses.quantity')}</span>
          <span>{i18n.t('expenses.price')}</span>
          <span>{i18n.t('expenses.total')}</span>
          <span />
        </div>
        {form.items.map((item, index) => (
          <div className="expenseLine" key={index}>
            <label className="expenseLineField">
              <span>{i18n.t('generic.description')}</span>
              <input className="expenseInput" value={item.description ?? ''} onChange={(e) => setLine(index, { description: e.target.value })} />
            </label>
            <label className="expenseLineField">
              <span>{i18n.t('expenses.quantity')}</span>
              <input className="expenseInput" type="number" step="0.01" min="0" value={item.quantity} onChange={(e) => setLine(index, { quantity: Number(e.target.value || 0) })} />
            </label>
            <label className="expenseLineField">
              <span>{i18n.t('expenses.price')}</span>
              <input className="expenseInput" type="number" step="0.01" min="0" value={item.price} onChange={(e) => setLine(index, { price: Number(e.target.value || 0) })} />
            </label>
            <label className="expenseLineField">
              <span>{i18n.t('expenses.total')}</span>
              <input className="expenseInput isReadonly" value={formatMoney(item.total)} readOnly />
            </label>
            <button type="button" className="expenseIconBtn" onClick={() => removeLine(index)} disabled={form.items.length <= 1}>
              x
            </button>
          </div>
        ))}
      </div>

      <div className="expenseTotals">
        <div>
          <span>{i18n.t('expenses.amountWithoutVat')}</span>
          <strong>{formatMoney(totals.amountWithoutVat)}</strong>
        </div>
        <div>
          <span>{i18n.t('expenses.vatAmount')}</span>
          <strong>{formatMoney(totals.vatAmount)}</strong>
        </div>
        <div className="isGrand">
          <span>{i18n.t('expenses.grandTotal')}</span>
          <strong>{formatMoney(totals.totalAmount)}</strong>
        </div>
      </div>
    </div>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode; className?: string }> = ({ label, children, className = '' }) => (
  <label className={`expenseField ${className}`.trim()}>
    <span>{label}</span>
    {children}
  </label>
);

const roundMoney = (value: number) => Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;
const formatMoney = (value: number) => roundMoney(value).toFixed(2);

export default Expenses;
